/**
 * AMSettings — Account Manager settings page
 *
 * Manages notification preferences stored in am_notification_preferences.
 */

import { useState, useEffect } from "react";
import { AMLayout } from "@/components/am/AMLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Bell, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAccountManager } from "@/hooks/useAccountManager";
import { toast } from "sonner";

interface NotificationPrefs {
  notify_new_message: boolean;
  notify_sla_warning: boolean;
  notify_health_alert: boolean;
  notify_task_reminder: boolean;
  notify_client_login: boolean;
  email_enabled: boolean;
}

const DEFAULT_PREFS: NotificationPrefs = {
  notify_new_message: true,
  notify_sla_warning: true,
  notify_health_alert: true,
  notify_task_reminder: true,
  notify_client_login: false,
  email_enabled: false,
};

const PREF_LABELS: Record<keyof Omit<NotificationPrefs, "email_enabled">, { label: string; description: string }> = {
  notify_new_message: {
    label: "New messages",
    description: "Get notified when a client sends you a message",
  },
  notify_sla_warning: {
    label: "SLA warnings & breaches",
    description: "Alert when response time approaches or exceeds SLA threshold",
  },
  notify_health_alert: {
    label: "Health score alerts",
    description: "Notify when a client's health score drops significantly",
  },
  notify_task_reminder: {
    label: "Task reminders",
    description: "Reminders for upcoming and overdue tasks",
  },
  notify_client_login: {
    label: "Client login activity",
    description: "Know when your clients log in to the platform",
  },
};

export default function AMSettings() {
  const { accountManager, loading: amLoading } = useAccountManager();
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!accountManager) return;

    const fetchPrefs = async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data } = await supabase
        .from("am_notification_preferences")
        .select("*")
        .eq("user_id", user.user.id)
        .maybeSingle();

      if (data) {
        setPrefs({
          notify_new_message: data.notify_new_message ?? true,
          notify_sla_warning: data.notify_sla_warning ?? true,
          notify_health_alert: data.notify_health_alert ?? true,
          notify_task_reminder: data.notify_task_reminder ?? true,
          notify_client_login: data.notify_client_login ?? false,
          email_enabled: data.email_enabled ?? false,
        });
      }
      setLoading(false);
    };
    fetchPrefs();
  }, [accountManager]);

  const updatePref = async (key: keyof NotificationPrefs, value: boolean) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
    setSaving(true);

    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      setSaving(false);
      return;
    }

    const { error } = await supabase
      .from("am_notification_preferences")
      .upsert(
        {
          user_id: user.user.id,
          [key]: value,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );

    setSaving(false);

    if (error) {
      console.error("[AMSettings] Failed to update preference:", error);
      toast.error("Failed to save preference");
      setPrefs((prev) => ({ ...prev, [key]: !value }));
    }
  };

  if (amLoading || loading) {
    return (
      <AMLayout>
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading settings...
        </div>
      </AMLayout>
    );
  }

  return (
    <AMLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your notification preferences
          </p>
        </div>

        {/* In-app notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4" />
              In-App Notifications
            </CardTitle>
            <CardDescription>
              Choose which notifications appear in your notification bell
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {(Object.entries(PREF_LABELS) as [keyof typeof PREF_LABELS, { label: string; description: string }][]).map(
              ([key, { label, description }]) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor={key} className="text-sm font-medium">
                      {label}
                    </Label>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    id={key}
                    checked={prefs[key]}
                    onCheckedChange={(checked) => updatePref(key, checked)}
                    disabled={saving}
                  />
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* Email notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Notifications
            </CardTitle>
            <CardDescription>
              Receive email summaries for important events
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="email_enabled" className="text-sm font-medium">
                  Enable email notifications
                </Label>
                <p className="text-xs text-muted-foreground">
                  Get email alerts for SLA breaches and critical health score changes
                </p>
              </div>
              <Switch
                id="email_enabled"
                checked={prefs.email_enabled}
                onCheckedChange={(checked) => updatePref("email_enabled", checked)}
                disabled={saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* AM Profile info */}
        {accountManager && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name</span>
                <span className="font-medium">{accountManager.full_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{accountManager.email}</span>
              </div>
              {accountManager.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span className="font-medium">{accountManager.phone}</span>
                </div>
              )}
              {accountManager.title && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Title</span>
                  <span className="font-medium">{accountManager.title}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AMLayout>
  );
}
